import styles from "./styles.module.scss"
import { Music2, Pause, Play, RotateCcw, RotateCw } from "lucide-react"
import { Slider } from "@/components/ui/slider"
import { useEffect, useRef, useState } from "react"
import { formatAudioTime } from "@/shared/utils/format-audio-time"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Progress } from "@/components/ui/progress"

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  AUDIO_SPEED_PRESETS,
  LANG_KEYS,
  ROUTER_PATHS,
} from "@/shared/constants"
import { observer } from "mobx-react-lite"
import { activePlayer } from "@/entities/audio-engine"
import { useAutoTranslation, type AudioPlaybackRate } from "@/entities"
import { useNavigate } from "react-router-dom"

export const CommonPlayer = observer(() => {
  const { t } = useAutoTranslation()
  const {
    isPlaying,
    isLoading,
    isWaitingForChunk,
    isSeeking,
    playbackRate,
    title,
    totalDuration,
    currentPosition,
    isActive: isActiveAudio,
    id: activeId,
  } = activePlayer
  const navigate = useNavigate()

  const handleOpenDetails = () => {
    if (!activeId) return
    navigate(ROUTER_PATHS.APP_LIBRARY_DETAILS.replace(":id", activeId))
  }

  const [speedPopoverOpen, setSpeedPopoverOpen] = useState(false)
  const [dragPosition, setDragPosition] = useState<number | null>(null)

  // Debounce isLoading — only show loading UI if loading takes > 500ms
  const [showLoading, setShowLoading] = useState(false)
  const loadingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (isLoading) {
      loadingTimerRef.current = setTimeout(() => {
        setShowLoading(true)
      }, 500)
    } else {
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current)
        loadingTimerRef.current = null
      }
      setShowLoading(false)
    }
    return () => {
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current)
      }
    }
  }, [isLoading])

  const displayPosition = dragPosition ?? currentPosition
  const sliderValue =
    totalDuration > 0 ? (displayPosition / totalDuration) * 100 : 0

  const handlePressForwardLeft = () => {
    seekAndPlay(Math.max(0, currentPosition - 15))
  }

  //TODO
  //   const handleOpenSleepTimer = () => {
  //     console.log("handleOpenSleepTimer")
  //   }

  const handleSpeedChange = (speed: AudioPlaybackRate) => {
    activePlayer.setPlaybackRate(speed)
    setSpeedPopoverOpen(false)
  }

  const handleChangePosition = (value: number[]) => {
    setDragPosition((value[0] / 100) * totalDuration)
  }

  const handleCommitPosition = (value: number[]) => {
    const newPosition = (value[0] / 100) * totalDuration
    setDragPosition(null)
    activePlayer.seekToAndPlay(newPosition)
  }

  const seekAndPlay = (newPosition: number) => {
    activePlayer.seekToAndPlay(newPosition)
  }

  const handlePressForwardRight = () => {
    seekAndPlay(Math.min(totalDuration, currentPosition + 30))
  }

  const handlePressPlay = () => {
    if (isWaitingForChunk) return
    if (isPlaying) {
      activePlayer.pause()
    } else {
      activePlayer.resume()
    }
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.info}>
        <div className={styles.info__left}>
          <div className="from-primary/20 to-primary/5 relative flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br">
            <Music2 className="text-primary/60 h-5 w-5" />
          </div>
        </div>
        <div className={styles.info__right}>
          <h4
            className={cn(
              styles.info__right__title,
              isActiveAudio && activeId && "cursor-pointer hover:underline"
            )}
            title={title ?? ""}
            onClick={isActiveAudio && activeId ? handleOpenDetails : undefined}
          >
            {title}
          </h4>
          {isActiveAudio && (
            <span className="text-muted-foreground flex items-center gap-2 text-xs">
              {formatAudioTime(totalDuration)}
            </span>
          )}
        </div>
      </div>

      <div
        className={cn(styles.player, {
          [styles.player_disabled]: !isActiveAudio,
        })}
      >
        <div className={styles.controls}>
          <button
            type="button"
            className="flex cursor-pointer flex-col text-xs disabled:opacity-50"
            disabled={!isActiveAudio}
            onClick={handlePressForwardLeft}
          >
            <RotateCcw size={16} />
            <span>15</span>
          </button>
          <Button
            className={cn(
              styles.controls__playWrapper,
              isPlaying && "shadow-[0_0_20px_oklch(0.623_0.214_259.815/0.4)]"
            )}
            disabled={!isActiveAudio || isWaitingForChunk}
            onClick={() => handlePressPlay()}
            isLoading={isLoading || isSeeking || isWaitingForChunk}
          >
            {isPlaying ? <Pause size={18} /> : <Play size={18} />}
          </Button>
          <button
            type="button"
            className="flex cursor-pointer flex-col text-xs disabled:opacity-50"
            disabled={!isActiveAudio}
            onClick={handlePressForwardRight}
          >
            <RotateCw size={16} />
            <span>30</span>
          </button>
        </div>

        <div className={styles.player__inner}>
          <h4 className={styles.player__time}>
            {!!isActiveAudio && formatAudioTime(displayPosition)}
          </h4>

          {showLoading ? (
            <div className={styles.sliderWrapper}>
              <Progress indeterminate className="h-2 w-full" />
            </div>
          ) : (
            <div
              className={styles.sliderWrapper}
              onClick={e => {
                if (!isActiveAudio) return

                const target = e.target as HTMLElement
                const clickedThumb = target.closest(
                  '[data-slot="slider-thumb"]'
                )
                const clickedTrack = target.closest(
                  '[data-slot="slider-track"]'
                )

                // Radix Slider handles thumb drags AND track clicks itself
                // (pointerdown moves the thumb, onValueCommit seeks on
                // release). Handling track clicks here too fired
                // seekToAndPlay twice per click — the second resume() raced
                // howler's async isPlaying flip and spawned a second
                // simultaneous sound (double audio).
                if (clickedThumb || clickedTrack) {
                  return
                }

                if (target === e.currentTarget) {
                  const sliderElement = e.currentTarget
                  const rect = sliderElement.getBoundingClientRect()
                  const clickX = e.clientX - rect.left
                  const percentage = Math.max(
                    0,
                    Math.min(100, (clickX / rect.width) * 100)
                  )
                  const newPosition = (percentage / 100) * totalDuration
                  seekAndPlay(newPosition)
                }
              }}
            >
              <Slider
                value={[sliderValue]}
                max={100}
                onValueChange={handleChangePosition}
                onValueCommit={handleCommitPosition}
                disabled={!isActiveAudio}
              />
            </div>
          )}
          <h4 className={styles.player__time}>
            {isActiveAudio && formatAudioTime(totalDuration)}
          </h4>

          <div className={styles.extraControls}>
            <Popover open={speedPopoverOpen} onOpenChange={setSpeedPopoverOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className={cn(
                    styles.extraControls__button,
                    "disabled:opacity-50"
                  )}
                  disabled={!isActiveAudio}
                >
                  <span className={styles.extraControls__label}>
                    {t(LANG_KEYS.SPEED)}
                  </span>
                  <span className={styles.extraControls__value}>
                    {playbackRate}x
                  </span>
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-2" align="end">
                <div className="grid grid-cols-5 gap-1">
                  {AUDIO_SPEED_PRESETS.map(speed => (
                    <button
                      key={speed}
                      type="button"
                      className={cn(
                        "hover:bg-accent rounded px-2 py-1.5 text-sm font-medium transition-colors",
                        playbackRate === speed &&
                          "bg-primary text-primary-foreground hover:bg-primary/90"
                      )}
                      onClick={() => handleSpeedChange(speed)}
                    >
                      {speed}x
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>

            {/* <button
              type="button"
              className={cn(
                styles.extraControls__button,
                "disabled:opacity-50"
              )}
              disabled={!isActiveAudio}
              onClick={handleOpenSleepTimer}
            >
              <Moon size={14} />
              <span className={styles.extraControls__label}>
                {sleepTimerRemaining ? sleepTimerRemaining : "Sleep"}
              </span>
            </button> */}
          </div>
        </div>
      </div>
    </div>
  )
})
